import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useId, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';

import { CatalogPickDialog } from './CatalogPickDialog';
import {
  GYM_CATALOG_KEY,
  GYM_ROWS_PER_MAKE,
  keepGymItems,
  linkGymExercises,
  linkGymFoods,
} from './gym-catalog-query';

export interface GymGroupActionsProps {
  readonly kind: 'food' | 'exercise';
  readonly name: string;
  readonly ids: readonly string[];
  /** "Make a catalog food": the console's own create page, named for the group. */
  readonly onMake: (name: string, ids: readonly string[]) => void;
}

/**
 * A group's three decisions. Making a catalog row is the console's own create
 * page (opened by the route), linked back here once it exists; the other two
 * are decided in place. Each leaves the gym rows as they are.
 */
export function GymGroupActions({ kind, name, ids, onMake }: GymGroupActionsProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const noteId = useId();
  const [linking, setLinking] = useState(false);
  const [keeping, setKeeping] = useState(false);
  const [note, setNote] = useState('');

  const settled = () => {
    setLinking(false);
    setKeeping(false);
    setNote('');
    void queryClient.invalidateQueries({ queryKey: GYM_CATALOG_KEY });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'scope-counts'] });
  };

  const link = useMutation({
    mutationFn: (catalogId: string) =>
      kind === 'food' ? linkGymFoods(api, ids, catalogId) : linkGymExercises(api, ids, catalogId),
    onSuccess: settled,
    retry: false,
  });

  const keep = useMutation({
    mutationFn: () => keepGymItems(api, kind === 'food' ? 'foods' : 'exercises', ids, note.trim()),
    onSuccess: settled,
    retry: false,
  });

  if (!can.mutate) return null;
  const noun = kind === 'food' ? 'food' : 'exercise';
  // The create page carries the group's ids in its URL, and no more than this.
  const tooManyToMake = ids.length > GYM_ROWS_PER_MAKE;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="outline"
        size="xs"
        disabled={tooManyToMake}
        onClick={() => {
          onMake(name, ids);
        }}
      >
        <Sparkles aria-hidden="true" className="size-3.5" />
        Make a catalog {noun}
      </Button>
      <Button
        variant="outline"
        size="xs"
        onClick={() => {
          setLinking(true);
        }}
      >
        Same as a catalog {noun}…
      </Button>
      <Button
        variant="ghost"
        size="xs"
        onClick={() => {
          setKeeping(true);
        }}
      >
        Keep it the gyms’ own…
      </Button>
      {tooManyToMake ? (
        <p className="w-full text-xs text-muted-foreground">
          {ids.length} gym rows are more than one create page takes ({GYM_ROWS_PER_MAKE}). Create the catalog{' '}
          {noun} first, then use “Same as a catalog {noun}”, which links them all.
        </p>
      ) : null}

      {linking ? (
        <CatalogPickDialog
          kind={kind}
          title={`Same as a catalog ${noun}?`}
          body={
            <p>
              Pick the catalog {noun} “{name}” is. Each gym keeps its own row, so no plan or log changes;
              their trainers are told Steado has a checked version.
            </p>
          }
          initialTerm={name}
          confirmLabel="Link"
          isPending={link.isPending}
          error={link.isError ? link.error.message : undefined}
          onConfirm={(catalogId) => {
            link.mutate(catalogId);
          }}
          onCancel={() => {
            setLinking(false);
            link.reset();
          }}
        />
      ) : null}

      {keeping ? (
        <ConfirmDialog
          title="Keep it the gyms’ own?"
          body={
            <div className="flex flex-col gap-3">
              <p>
                “{name}” leaves the queue and stays only in the gyms that added it. If a gym edits it later,
                it comes back.
              </p>
              <div className="flex flex-col gap-2">
                <Label htmlFor={noteId} className="text-xs font-medium text-muted-foreground">
                  Note to the gyms (their owners see it)
                </Label>
                <Input
                  id={noteId}
                  value={note}
                  maxLength={500}
                  onChange={(event) => {
                    setNote(event.target.value);
                  }}
                  placeholder="Your house blend, so it stays your gym’s own"
                />
              </div>
            </div>
          }
          confirmLabel="Keep"
          confirmDisabled={note.trim() === ''}
          onConfirm={() => {
            keep.mutate();
          }}
          onCancel={() => {
            setKeeping(false);
            setNote('');
            keep.reset();
          }}
          isPending={keep.isPending}
          error={keep.isError ? keep.error.message : undefined}
        />
      ) : null}
    </div>
  );
}
